import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Flex, { AlignItems, FlexDirection, JustifyContent } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { navigate } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { AuthLayout } from "@/features/authentication/auth-layout";
import { logout } from "@/server/auth-actions";

/**
 * Application states that are neither signed out nor signed in.
 *
 * A verified identity with no workspace membership is not an authentication
 * failure, and showing the login screen again would tell that person nothing.
 */
export function NoWorkspace({ name }: { name: string }) {
  const cache = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const invitations = useQuery({
    queryKey: ["pending-invitations", name],
    queryFn: async (): Promise<Array<{ id: string; workspaceName: string; role: string }>> => {
      const response = await fetch("/api/auth/pending-invitations", { credentials: "include" });
      if (!response.ok) throw new Error("Could not load your invitations.");
      return response.json();
    },
  });
  const accept = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(
        `/api/auth/pending-invitations/${encodeURIComponent(id)}/accept`,
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ name: displayName.trim() || name }),
        },
      );
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "Could not accept this invitation.");
      return body.workspaceId as string;
    },
    onSuccess: async (workspaceId) => {
      await cache.invalidateQueries({ queryKey: ["session"] });
      await cache.invalidateQueries({ queryKey: ["pending-invitations"] });
      navigate(`/w/${workspaceId}`);
    },
  });
  return (
    <AuthLayout>
      <Flex
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        gap={12}
      >
        <Text size={TextSize.HEADING_SM} weight={TextWeight.MEDIUM}>
          {invitations.data?.length ? "You’re invited" : "No workspace yet"}
        </Text>
        <Text size={TextSize.BODY_MD} variant={TextVariant.SECONDARY}>
          {invitations.isPending
            ? "Checking your invitations…"
            : invitations.data?.length
              ? `You are signed in as ${name}. Accept an invitation to join your team.`
              : invitations.isError
                ? "Your account is verified. We could not check your invitations."
                : `${name}, your account is verified but you are not a member of a workspace. Open your invitation link or ask an administrator to invite this email address.`}
        </Text>
        {invitations.isError && (
          <Button label="Try again" onClick={() => void invitations.refetch()} />
        )}
        {!!invitations.data?.length && (
          <>
            <TextInput label="Your name" value={displayName} onChange={setDisplayName} />
            {invitations.data.map((invitation) => (
              <Button
                key={invitation.id}
                label={`Join ${invitation.workspaceName} as ${invitation.role === "admin" ? "Admin" : "Member"}`}
                isDisabled={accept.isPending}
                isLoading={accept.isPending && accept.variables === invitation.id}
                onClick={() => accept.mutate(invitation.id)}
              />
            ))}
          </>
        )}
        {accept.isError && <Alert variant={AlertVariant.ERROR}>{accept.error.message}</Alert>}
        <Button label="Sign out" variant={ButtonVariant.SECONDARY} onClick={() => void logout()} />
      </Flex>
    </AuthLayout>
  );
}

export function Unavailable({ message }: { message: string }) {
  return (
    <AuthLayout>
      <Flex
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        gap={12}
      >
        <Text size={TextSize.HEADING_SM} weight={TextWeight.MEDIUM}>
          Cannot reach the service
        </Text>
        <Text size={TextSize.BODY_MD} variant={TextVariant.SECONDARY}>
          {message}
        </Text>
      </Flex>
    </AuthLayout>
  );
}
